import '../styles/main.scss';
import { tsParticles } from "@tsparticles/engine";
import { loadFull } from "tsparticles";
import gsap from 'gsap';

// Initialize Colorful Particles
(async () => {
  await loadFull(tsParticles);

  await tsParticles.load({
    id: "tsparticles",
    options: {
      background: {
        color: {
          value: "transparent"
        }
      },
      fpsLimit: 60,
      particles: {
        color: {
          value: ["#FF6B6B", "#FFE66D", "#4ECDC4", "#45B7D1", "#A855F7", "#FF8C94"]
        },
        move: {
          enable: true,
          direction: "none",
          outModes: {
            default: "out"
          },
          speed: 1,
          random: true,
          straight: false
        },
        number: {
          value: 80,
          density: {
            enable: true,
            area: 800
          }
        },
        opacity: {
          value: { min: 0.2, max: 0.7 },
          animation: {
            enable: true,
            speed: 1,
            sync: false
          }
        },
        shape: {
          type: "circle"
        },
        size: {
          value: { min: 1, max: 4 },
          animation: {
            enable: true,
            speed: 2,
            sync: false
          }
        },
        twinkle: {
          particles: {
            enable: true,
            frequency: 0.05,
            opacity: 1,
            color: {
              value: ["#FFFFFF", "#FFE66D", "#4ECDC4"]
            }
          }
        }
      },
      interactivity: {
        events: {
          onHover: {
            enable: true,
            mode: "grab"
          }
        },
        modes: {
          grab: {
            distance: 140,
            links: {
              opacity: 0.3,
              color: "#4ECDC4"
            }
          }
        }
      },
      detectRetina: true
    },
  });
})();

// Big Bang flash effect - expands from center to fill screen
function createBigBangFlash() {
  const flash = document.getElementById('bigbang-flash');
  const viewportSize = Math.max(window.innerWidth, window.innerHeight) * 3;
  
  gsap.fromTo(flash, 
    { 
      width: 0, 
      height: 0,
      opacity: 1
    },
    { 
      width: viewportSize, 
      height: viewportSize,
      opacity: 0,
      duration: 0.8,
      ease: "power2.out"
    }
  );
}

// Big Bang particle explosion - colorful rainbow particles
function createBigBangParticles() {
  const container = document.getElementById('bigbang-particles');
  const colors = ['#FF6B6B', '#FFE66D', '#4ECDC4', '#45B7D1', '#A855F7', '#FF8C94', '#FFFFFF', '#F472B6'];
  const particleCount = 80;
  const particles = [];
  
  for (let i = 0; i < particleCount; i++) {
    const particle = document.createElement('div');
    particle.className = 'explosion-particle';
    
    // Random properties
    const size = Math.random() * 6 + 2;
    const color = colors[Math.floor(Math.random() * colors.length)];
    const angle = (Math.PI * 2 * i) / particleCount + (Math.random() - 0.5) * 0.3;
    const distance = Math.random() * 600 + 300; // Larger distance for full screen
    const duration = Math.random() * 0.6 + 0.5;
    
    particle.style.cssText = `
      width: ${size}px;
      height: ${size}px;
      background: ${color};
      box-shadow: 0 0 ${size * 3}px ${color};
      left: 0;
      top: 0;
      transform: translate(-50%, -50%);
    `;
    
    container.appendChild(particle);
    particles.push({ element: particle, angle, distance, duration });
  }
  
  // Animate particles outward
  particles.forEach((p, index) => {
    const targetX = Math.cos(p.angle) * p.distance;
    const targetY = Math.sin(p.angle) * p.distance;
    
    gsap.to(p.element, {
      x: targetX,
      y: targetY,
      opacity: 0,
      scale: 0.1,
      duration: p.duration,
      ease: "power2.out",
      delay: index * 0.003,
      onComplete: () => {
        p.element.remove();
      }
    });
  });
}

// Opening Animation - Logo appears, Big Bang, then moves up seamlessly
document.addEventListener('DOMContentLoaded', () => {
  const overlay = document.getElementById('opening-overlay');
  const openingLogo = document.querySelector('.opening-logo');
  const mainTitle = document.querySelector('.hero h1');
  const mainContent = document.querySelector('.main-content');
  
  // Set initial states - both logos same size now
  gsap.set(mainTitle, { visibility: 'hidden' });
  gsap.set(mainContent, { visibility: 'visible' });
  gsap.set(openingLogo, { opacity: 0, scale: 0.8 });
  
  // Get the target position dynamically
  const getTargetPosition = () => {
    const mainTitleRect = mainTitle.getBoundingClientRect();
    const openingLogoRect = openingLogo.getBoundingClientRect();
    return {
      y: mainTitleRect.top + mainTitleRect.height / 2 - (openingLogoRect.top + openingLogoRect.height / 2)
    };
  };
  
  // Create a timeline
  const tl = gsap.timeline();
  
  // Opening sequence with Big Bang flash
  tl.to(openingLogo, {
    duration: 0.4,
    scale: 1,
    opacity: 1,
    ease: "power2.out",
    onStart: () => {
      // Trigger Big Bang flash immediately
      createBigBangFlash();
      createBigBangParticles();
    }
  })
  // Brief hold
  .to(openingLogo, {
    duration: 0.5,
    scale: 1,
    ease: "none"
  })
  // Move opening logo UP to the main title position
  .to(openingLogo, {
    duration: 0.6,
    y: () => getTargetPosition().y,
    ease: "power2.inOut"
  })
  // Fade overlay background
  .to(overlay, {
    duration: 0.4,
    background: 'transparent',
    ease: "power2.out"
  }, "-=0.4")
  // Seamless instant swap (both are same size now, so no jitter)
  .call(() => {
    gsap.set(mainTitle, { visibility: 'visible', opacity: 1 });
    gsap.set(openingLogo, { opacity: 0 });
    overlay.style.display = 'none';
  })
  // Subtitle
  .fromTo('.subtitle', 
    { y: 15, opacity: 0 },
    { 
      duration: 0.5,
      y: 0,
      opacity: 0.85,
      ease: "power3.out"
    },
    "+=0.05"
  )
  // Cards
  .fromTo('.portal-card', 
    { y: 30, opacity: 0 },
    { 
      duration: 0.5,
      y: 0,
      opacity: 1,
      stagger: 0.1,
      ease: "power2.out"
    },
    "-=0.2"
  )
  // Footer
  .to('.footer', {
    duration: 0.4,
    autoAlpha: 1,
    ease: "power2.out"
  }, "-=0.1");
});
